import * as React from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { ClickAwayListener, Tooltip } from '@mui/material';
import COMMON from 'constants/common';
import { useAppSelector } from '../../../../redux/hooks';

/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  cellStyle: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-all',
  },
  displayStyle: {
    display: 'flex',
    flexDirection: 'row',
  },
  tooltipParent: {
    flex: 1,
  },
  customTooltip: {
    maxWidth: 620,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.2)',
    fontSize: '21px',
  },
}));
type TooltipProps = {
  tooltipText: string | undefined | null;
  displayWord?: string;
  lineClamp?: string;
  subfixText?: string;
  isBlank?: boolean;
};
const TooltipComponent: React.FC<React.PropsWithChildren<TooltipProps>> = (props) => {
  const { tooltipText, displayWord, lineClamp, subfixText, isBlank = false } = props;
  const TOOLTIP_TEXT = React.useMemo(
    () => (tooltipText === COMMON.BLANK || tooltipText === COMMON.BLANK3 ? null : tooltipText),
    [tooltipText],
  );

  const DISPLAY_WORD = React.useMemo(
    () => ((displayWord ?? TOOLTIP_TEXT) === '' ? null : displayWord ?? TOOLTIP_TEXT),
    [displayWord, TOOLTIP_TEXT],
  );

  // Inline style for `-webkit-line-clamp`, ensuring it's either a number or undefined
  const dynamicStyles = {
    WebkitLineClamp: lineClamp ? parseInt(lineClamp, 10) : 1,
  } as React.CSSProperties;

  const { classes } = useStyles();

  const [open, setOpen] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);

  // Handlers to set the hovered state
  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };
  const handleTooltipOpen = () => {
    setOpen(true);
  };

  const { isMobile } = useAppSelector((state) => state.common.deviceInfo);
  if (isMobile) {
    return (
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <div className={classes.tooltipParent}>
          {open ? (
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              classes={{ tooltip: classes.customTooltip }}
              onClose={handleTooltipClose}
              open={open}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              enterDelay={500}
              leaveDelay={300}
              title={TOOLTIP_TEXT ?? ''}
              placement="bottom-start">
              <div className={classes.displayStyle}>
                <div className={classes.cellStyle} style={dynamicStyles} onClick={handleTooltipOpen}>
                  <span>
                    {`${DISPLAY_WORD ?? (isBlank ? '\u00A0' : COMMON.BLANK)}`}
                    {subfixText && `${subfixText}`}
                  </span>
                </div>
                {subfixText && <div>{subfixText}</div>}
              </div>
            </Tooltip>
          ) : (
            <div className={classes.displayStyle}>
              <div className={classes.cellStyle} style={dynamicStyles} onClick={handleTooltipOpen}>
                <span>
                  {`${DISPLAY_WORD ?? (isBlank ? '\u00A0' : COMMON.BLANK)}`}
                  {subfixText && `${subfixText}`}
                </span>
              </div>
              {subfixText && <div>{subfixText}</div>}
            </div>
          )}
        </div>
      </ClickAwayListener>
    );
  }
  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {hovered ? (
        <Tooltip
          classes={{ tooltip: classes.customTooltip }}
          disableFocusListener
          disableTouchListener
          title={TOOLTIP_TEXT ?? ''}
          enterDelay={500}
          leaveDelay={300}
          placement="bottom-start">
          <div className={classes.displayStyle}>
            <div className={classes.cellStyle} style={dynamicStyles}>
              <span>{DISPLAY_WORD ?? (isBlank ? '\u00A0' : COMMON.BLANK)}</span>
            </div>
            {subfixText && <div>{subfixText}</div>}
          </div>
        </Tooltip>
      ) : (
        <div className={classes.displayStyle}>
          <div className={classes.cellStyle} style={dynamicStyles}>
            <span>{DISPLAY_WORD ?? (isBlank ? '\u00A0' : COMMON.BLANK)}</span>
          </div>
          {subfixText && <div>{subfixText}</div>}
        </div>
      )}
    </div>
  );
};
export default React.memo(TooltipComponent);
